const TEXTBOOK_FIGURE_TEXT_FLOW_PLACEMENTS =
  new Set([
    "half_left",
    "half_right",
    "small_left",
    "small_right",
  ]);

function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

export function isTextbookFigureTextFlowPlacement(
  value
) {
  return (
    TEXTBOOK_FIGURE_TEXT_FLOW_PLACEMENTS
      .has(
        clean(value)
      )
  );
}

function sameSection(first, second) {
  return (
    clean(first?.sectionItemId) ===
    clean(second?.sectionItemId)
  );
}

/*
 * Accepted proposals are stitched into Canon as
 * proposal-adoption-* paragraph blocks.
 *
 * They are a hard continuation boundary for automatic cursor
 * Figure wrapping. Otherwise, appending an accepted proposal
 * immediately after a side Figure changes the meaning of that
 * pre-existing Figure and makes the new proposal write across it.
 *
 * Explicit/saved legacy anchors remain readable; this rule only
 * prevents newly adjacent proposal-adoption text from becoming an
 * automatic cursor follower.
 */
function cursorFollowingParagraph(
  block
) {
  return (
    clean(block?.type) ===
      "paragraph" &&
    !clean(block?.blockId)
      .startsWith(
        "proposal-adoption-"
      )
  );
}

function richManuscriptAnchorExists(
  textBlock,
  figureBlock
) {
  const anchorId =
    clean(
      figureBlock
        ?.figureTextFlowAnchorId
    );

  if (
    !anchorId ||
    clean(textBlock?.type) !==
      "paragraph" ||
    clean(figureBlock?.type) !==
      "figure" ||
    !isTextbookFigureTextFlowPlacement(
      figureBlock?.figurePlacement
    ) ||
    !sameSection(
      textBlock,
      figureBlock
    )
  ) {
    return false;
  }

  const internalBlocks =
    Array.isArray(
      textBlock
        ?.manuscriptContent
        ?.blocks
    )
      ? textBlock
          .manuscriptContent
          .blocks
      : [];

  return internalBlocks.some(
    (item) =>
      clean(item?.id) ===
        anchorId &&
      clean(item?.type) ===
        "paragraph"
  );
}

function firstRichParagraphId(
  textBlock
) {
  if (
    clean(textBlock?.type) !==
    "paragraph"
  ) {
    return "";
  }

  const internalBlocks =
    Array.isArray(
      textBlock
        ?.manuscriptContent
        ?.blocks
    )
      ? textBlock
          .manuscriptContent
          .blocks
      : [];

  const first =
    internalBlocks.find(
      (item) =>
        clean(item?.type) ===
          "paragraph" &&
        clean(item?.id)
    ) || null;

  return clean(first?.id);
}

function lastRichParagraphId(
  textBlock
) {
  if (
    clean(textBlock?.type) !==
    "paragraph"
  ) {
    return "";
  }

  const internalBlocks =
    Array.isArray(
      textBlock
        ?.manuscriptContent
        ?.blocks
    )
      ? textBlock
          .manuscriptContent
          .blocks
      : [];

  const paragraphs =
    internalBlocks.filter(
      (item) =>
        clean(item?.type) ===
          "paragraph" &&
        clean(item?.id)
    );

  const last =
    paragraphs[
      paragraphs.length - 1
    ] || null;

  return clean(last?.id);
}


/*
 * V1E Figure-flow authority.
 *
 * Writer-facing rule:
 *   the cursor position is the Figure anchor.
 *
 * Legacy saved figureTextFlowAnchorId remains readable only
 * as compatibility plumbing.
 *
 * If an older same-section side Figure follows rich Text but its
 * saved legacy anchor is missing or no longer resolves, presentation
 * may recover conservatively at that Text block's last rich paragraph.
 * Recovery is used only when the Figure is not already eligible for
 * the new cursor rule with a following same-section paragraph.
 * This recovery is projection-only and does not rewrite persistence.
 *
 * New cursor path:
 *   side Figure -> immediately following rich Text.
 * That Text block's first rich paragraph becomes the automatic
 * wrap anchor.
 *
 * Plain-text fallback consumes one following paragraph only.
 */
export function buildTextbookFigureTextFlowUnits(
  value
) {
  const blocks =
    Array.isArray(value)
      ? value
      : [];

  const units = [];
  let index = 0;

  while (index < blocks.length) {
    const block = blocks[index];
    const next = blocks[index + 1];
    const nextNext =
      blocks[index + 2];

    if (
      next &&
      richManuscriptAnchorExists(
        block,
        next
      )
    ) {
      units.push({
        kind:
          "rich_manuscript_figure_flow",
        paragraph: block,
        figure: next,
        anchorId:
          clean(
            next
              .figureTextFlowAnchorId
          ),
        authority:
          "legacy_saved_anchor",
      });

      index += 2;
      continue;
    }

    const nextIsSideFigure =
      clean(next?.type) ===
        "figure" &&
      isTextbookFigureTextFlowPlacement(
        next?.figurePlacement
      );

    const nextFigureHasCursorFollowing =
      nextIsSideFigure &&
      nextNext &&
      cursorFollowingParagraph(
        nextNext
      ) &&
      sameSection(
        next,
        nextNext
      );

    if (
      nextIsSideFigure &&
      !nextFigureHasCursorFollowing &&
      clean(block?.type) ===
        "paragraph" &&
      sameSection(
        block,
        next
      )
    ) {
      const recoveredAnchorId =
        lastRichParagraphId(
          block
        );

      if (recoveredAnchorId) {
        units.push({
          kind:
            "rich_manuscript_figure_flow",
          paragraph: block,
          figure: next,
          anchorId:
            recoveredAnchorId,
          authority:
            "legacy_recovered_position",
        });

        index += 2;
        continue;
      }
    }

    const sideFigure =
      clean(block?.type) ===
        "figure" &&
      isTextbookFigureTextFlowPlacement(
        block?.figurePlacement
      );

    if (!sideFigure) {
      units.push({
        kind: "block",
        block,
      });

      index += 1;
      continue;
    }

    const following =
      next &&
      cursorFollowingParagraph(
        next
      ) &&
      sameSection(
        block,
        next
      )
        ? next
        : null;

    if (!following) {
      units.push({
        kind: "block",
        block,
      });

      index += 1;
      continue;
    }

    const anchorId =
      firstRichParagraphId(
        following
      );

    if (anchorId) {
      const additionalFigureFlows =
        [];

      let trailingIndex =
        index + 2;

      while (
        trailingIndex <
        blocks.length
      ) {
        const trailingFigure =
          blocks[
            trailingIndex
          ];

        if (
          clean(
            trailingFigure
              ?.type
          ) !==
            "figure" ||
          !isTextbookFigureTextFlowPlacement(
            trailingFigure
              ?.figurePlacement
          ) ||
          !sameSection(
            following,
            trailingFigure
          ) ||
          !richManuscriptAnchorExists(
            following,
            trailingFigure
          )
        ) {
          break;
        }

        additionalFigureFlows.push({
          figure:
            trailingFigure,
          anchorId:
            clean(
              trailingFigure
                .figureTextFlowAnchorId
            ),
          authority:
            "legacy_saved_anchor",
        });

        trailingIndex += 1;
      }

      units.push({
        kind:
          "cursor_figure_flow",
        figure: block,
        paragraph: following,
        anchorId,
        authority:
          "cursor",
        ...(
          additionalFigureFlows
            .length
            ? {
                additionalFigureFlows,
              }
            : {}
        ),
      });

      index =
        trailingIndex;
      continue;
    }

    units.push({
      kind:
        "figure_text_flow",
      figure: block,
      paragraphs: [
        following,
      ],
      authority:
        "cursor",
    });

    index += 2;
  }

  return units;
}
